import React, { useId, useState } from "react";
import { motion } from "framer-motion";
import { MinusIcon, PlusIcon } from "lucide-react";
import { business } from "../data/business";
import { useCountUp } from "../hooks/useCountUp";
import { estimateBail, formatCurrency } from "../utils/bail";
export function BailCalculator({
  inputId
}: {
  inputId?: string;
}) {
  const autoId = useId();
  const bailId = inputId ?? `bail-${autoId}`;
  const [raw, setRaw] = useState("5,000");
  const [cases, setCases] = useState(1);
  const bail = Number(raw.replace(/[^\d]/g, "")) || 0;
  const {
    premium,
    filing,
    total
  } = estimateBail(bail, cases);
  const premiumShown = useCountUp(premium);
  const filingShown = useCountUp(filing);
  const totalShown = useCountUp(total);
  function onBailChange(e: React.ChangeEvent<HTMLInputElement>) {
    const digits = e.target.value.replace(/[^\d]/g, "").slice(0, 9);
    setRaw(digits ? Number(digits).toLocaleString("en-US") : "");
  }
  const lines = [{
    label: "Bond premium",
    note: "15% of bail, $50 minimum",
    value: premium,
    shown: premiumShown
  }, {
    label: "Jail filing fees",
    note: `$50 × ${cases} case${cases > 1 ? "s" : ""}`,
    value: filing,
    shown: filingShown
  }];
  return <div className="grain relative overflow-hidden rounded-3xl border border-gold/30 bg-navy p-[clamp(1.5rem,3vw,2.75rem)] text-white shadow-lift" data-id="element-20" data-mp="1s5ya6gqkr9ur">
      <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 gold-glow" aria-hidden="true" data-id="element-21" data-mp="1w8us058aj1xn" />
      <div className="grid gap-5 sm:grid-cols-[1fr_auto]" data-id="element-22" data-mp="2ovfrxu2u0mct">
        <div data-id="element-23" data-mp="2ouxmc6ua4vs8">
          <label htmlFor={bailId} className="text-sm font-medium text-white/80" data-id="element-24" data-mp="2ount8adxu8ib">
            Total bail amount
          </label>
          <div className="mt-2 flex min-h-[56px] items-center rounded-xl border border-white/15 bg-white/[0.06] px-4 transition-[border-color] duration-150 focus-within:border-gold-soft" data-id="element-25" data-mp="2f5mfkquv837i">
            <span className="font-serif text-2xl text-gold-soft" aria-hidden="true" data-id="element-26" data-mp="32tdr3s8l0c7q">$</span>
            <input id={bailId} value={raw} onChange={onBailChange} inputMode="numeric" placeholder="0" className="w-full bg-transparent px-2 font-serif text-2xl text-white placeholder:text-white/30 focus:outline-none" aria-describedby={`${bailId}-hint`} data-id="element-27" data-mp="37nrrya60jbjs" />
          </div>
        </div>
        <div data-id="element-28" data-mp="33k8pvyc7r6c3">
          <span id={`${bailId}-cases`} className="text-sm font-medium text-white/80" data-id="element-29" data-mp="2yj1ii0dzg95y">Case numbers</span>
          <div className="mt-2 flex min-h-[56px] items-center rounded-xl border border-white/15 bg-white/[0.06]" role="group" aria-labelledby={`${bailId}-cases`} data-id="element-30" data-mp="2tscsyqlv5m9g">
            <button type="button" onClick={() => setCases(c => Math.max(1, c - 1))} aria-label="Fewer case numbers" disabled={cases <= 1} className="grid h-14 w-12 place-items-center text-white/80 transition-colors duration-150 hover:text-gold-soft disabled:opacity-30" data-id="element-31" data-mp="2pqzlkc34hb0f">
              <MinusIcon className="h-4 w-4" data-id="element-32" data-mp="20q1zf5wb95gx" />
            </button>
            <output className="w-10 text-center font-serif text-2xl" aria-live="polite" data-id="element-33" data-mp="20rwfo6wn09eo">{cases}</output>
            <button type="button" onClick={() => setCases(c => Math.min(20, c + 1))} aria-label="More case numbers" className="grid h-14 w-12 place-items-center text-white/80 transition-colors duration-150 hover:text-gold-soft" data-id="element-34" data-mp="2anq34lbw9nb8">
              <PlusIcon className="h-4 w-4" data-id="element-35" data-mp="1my58v6aaj3mn" />
            </button>
          </div>
        </div>
      </div>

      <dl className="mt-8 space-y-5 border-t border-gold/25 pt-6" data-id="element-36" data-mp="1rxi52hn1xn1w">
        {lines.map(l => <div key={l.label} data-id="element-37" data-mp="1wnoqy9q6itgg">
            <div className="flex items-baseline justify-between gap-4" data-id="element-38" data-mp="1wp0615p2rnmv">
              <dt data-id="element-39" data-mp="z8syi3388pir">
                <span className="text-white/90" data-id="element-40" data-mp="z9cirqgcnpzy">{l.label}</span>
                <span className="ml-2 text-sm text-white/50" data-id="element-41" data-mp="147nuk6q7gakt">{l.note}</span>
              </dt>
              <dd className="font-serif text-xl tabular-nums" data-id="element-42" data-mp="145b392yk4nv3">{formatCurrency(l.shown)}</dd>
            </div>
            <div className="mt-2 h-px w-full bg-white/10" data-id="element-43" data-mp="30xqlhf0h7ae5">
              <motion.div className="h-px bg-gold" initial={false} animate={{
            width: total ? `${l.value / total * 100}%` : "0%"
          }} transition={{
            duration: 0.6,
            ease: [0.22, 1, 0.36, 1]
          }} data-id="element-44" data-mp="30zbakp40u14m" />
            </div>
          </div>)}
        <div className="flex flex-wrap items-end justify-between gap-4 pt-2" data-id="element-45" data-mp="3ope2n57xr738">
          <dt className="eyebrow text-gold-soft" data-id="element-46" data-mp="3kl6bz2hx3h2d">Estimated total due</dt>
          <dd className="font-serif text-[clamp(2.5rem,4vw,4rem)] leading-none tabular-nums text-white" aria-live="polite" data-id="element-47" data-mp="2gcw3356986l">
            {formatCurrency(totalShown)}
          </dd>
        </div>
      </dl>

      {!bail && <p className="mt-4 text-sm text-white/60" data-id="element-48" data-mp="2489442s4rzwm">Enter the bail amount to see your estimate.</p>}

      <div className="mt-8 flex flex-wrap gap-3" data-id="element-49" data-mp="1z9ekiymca7bg">
        <a href={business.payUrl} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-[52px] items-center justify-center rounded-full bg-gold px-6 font-semibold text-navy transition-[transform,background-color] duration-150 ease-out hover:-translate-y-0.5 hover:bg-gold-hover active:scale-[0.98]" data-id="element-50" data-mp="eqc8i2ymsxn6">
          Pay This Amount Online
        </a>
        <a href={business.phoneHref} className="inline-flex min-h-[52px] items-center justify-center rounded-full border border-white/30 px-6 font-semibold text-white transition-[transform,border-color,color] duration-150 ease-out hover:-translate-y-0.5 hover:border-gold-soft hover:text-gold-soft active:scale-[0.98]" data-id="element-51" data-mp="3s5ibv2ju3g6t">
          Call to Discuss Payment Plans
        </a>
      </div>
      <p id={`${bailId}-hint`} className="mt-6 text-xs leading-relaxed text-white/55" data-id="element-52" data-mp="1gf6arueaf54n">
        Estimate only. Nevada law sets the premium at 15% of the bond (minimum $50). Call {business.phoneDisplay} to confirm your exact amount.
      </p>
    </div>;
}